import type { SponsorLeadListItem, SponsorLeadScope } from "~/lib/api";

const CSV_HEADERS = [
  "display_name",
  "company",
  "job_role",
  "headline",
  "email",
  "rating",
  "follow_up",
  "note",
  "scanned_at",
  "tt_ticket_id",
  "scanner_name",
] as const;

/** Escape a single CSV field (RFC 4180-ish). */
export function escapeCsvField(value: string): string {
  if (/[",\r\n]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

function cell(value: string | number | null | undefined): string {
  if (value == null) return "";
  return escapeCsvField(String(value));
}

/** Build CSV text for the current lead list (includes header row). */
export function buildSponsorLeadsCsv(items: SponsorLeadListItem[]): string {
  const lines = [CSV_HEADERS.join(",")];
  for (const item of items) {
    lines.push(
      [
        cell(item.display.display_name),
        cell(item.display.company),
        cell(item.display.job_role),
        cell(item.display.headline),
        cell(item.contact.email),
        cell(item.sponsor_lead.rating),
        cell(item.sponsor_lead.follow_up),
        cell(item.sponsor_lead.note),
        cell(item.scanned_at),
        cell(item.tt_ticket_id),
        cell(item.scanner_name),
      ].join(","),
    );
  }
  return `${lines.join("\n")}\n`;
}

function slugPart(raw: string): string {
  const s = raw
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return s || "leads";
}

/** Filename for a browser download, e.g. clickhouse-melbourne-2026-my-leads-2026-08-04.csv */
export function sponsorLeadsCsvFilename(opts: {
  sponsorOrg?: string | null;
  eventTitle: string;
  scope: SponsorLeadScope;
  now?: Date;
}): string {
  const now = opts.now ?? new Date();
  const ymd = now.toISOString().slice(0, 10);
  const org = opts.sponsorOrg?.trim() ? slugPart(opts.sponsorOrg) : "sponsor";
  const event = slugPart(opts.eventTitle);
  const scope = opts.scope === "team" ? "team-leads" : "my-leads";
  return `${org}-${event}-${scope}-${ymd}.csv`;
}

/** Trigger a browser file download for the given CSV body. */
export function downloadTextFile(filename: string, body: string, mime = "text/csv;charset=utf-8"): void {
  const blob = new Blob([body], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
